import {
  Button,
  Typography,
  useMediaQuery,
} from '@forgeglobal/design-framework';
import useStyles, { hasScrollBoxMediaQuery } from './style';
import { useState } from 'react';
import { EsignConsentStatus } from '@tcs/client-portal-api/platform_generated';

interface EsignConsentPageProps {
  handleClick: (status?: EsignConsentStatus) => void;
  hasScrolled: boolean;
  closeOnly?: boolean; // open dialog for read, not consent/decline
}

export const EsignConsentPage = ({
  handleClick,
  closeOnly = false,
  ...props
}: EsignConsentPageProps) => {
  const classes = useStyles();
  const [hasScrolled, setHasScrolled] = useState(props.hasScrolled);
  const hasScrollBox = useMediaQuery(hasScrollBoxMediaQuery);

  const handleScroll = (e) => {
    if (hasScrolled) {
      return;
    }
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 20;
    if (bottom) {
      setHasScrolled(true);
    }
  };

  return (
    <>
      <Typography variant="h5" className={classes.title}>
        E-SIGN Consent
      </Typography>
      <Typography
        variant="h6"
        color="textSecondary"
        className={classes.description}>
        Electronic Signatures in Global and National Commerce Act Disclosure and
        Consent
      </Typography>
      <div id="container" className={classes.container} onScroll={handleScroll}>
        <div>
          <b>Note:</b> You must read through the entire agreement before
          providing your consent.
        </div>
        <br />
        <p>
          Please carefully read this Electronic Signatures in Global and
          National Commerce Act (the <b>“E-SIGN”</b>) Disclosure and Consent.
          This document explains how you may choose to receive documents that
          are required by law or regulation to be provided in writing, including
          but not limited to, account statements, tax documents, notices,
          disclosures, privacy policies and other important documents from Forge
          Trust Co. (<b>“Forge Trust”</b>; such documents, collectively,{' '}
          <b>“Forge Trust Documents”</b>) electronically, including but without
          limitation, by email, online portal access and text message. By
          agreeing to receive Forge Trust Documents electronically, you agree
          that Forge Trust may keep certain records and information necessary to
          satisfy federal E-SIGN Act requirements.
        </p>
        <p>
          To receive Forge Trust Documents electronically, you must
          affirmatively agree by clicking the first option below. Your agreement
          applies to all Forge Trust Documents during your relationship with
          Forge Trust and is not limited to any particular transaction or
          document. Forge Trust primarily uses electronic processes, and you
          will receive the fastest responses and the most seamless experience by
          electing to receive Forge Trust Documents electronically.
        </p>
        <br />
        <h3>System Requirements to Access Information</h3>
        <p>
          To receive an electronic copy of Forge Trust Documents, you must have
          the following equipment and software:
        </p>
        <ul>
          <li>
            A personal computer or other device capable of accessing the
            Internet;
          </li>
          <li>
            An operating system on your device capable of receiving, accessing,
            and displaying communications in electronic form via email or
            through accessing{' '}
            <a href="https://forgetrust.com" target="blank">
              Forge Trust’s website
            </a>
            ;
          </li>
          <li>
            A current, compatible web browser, including the current or
            immediately preceding version of Chrome, Firefox, Safari or Edge;
          </li>
          <li>
            An operational cellular device capable of receiving SMS messages, as
            applicable (note that normal message rates may apply to SMS message
            receipt);
          </li>
          <li>A valid email account; and</li>
          <li>
            If you wish to store (or print) any Forge Trust Documents, a device
            capable of storing (or printing) Forge Trust Documents.
          </li>
        </ul>
        <p>
          Forge Trust reserves the right to modify these minimum system
          requirements from time to time as necessary. Forge Trust will contact
          you if the minimum system requirements materially change, in which
          case, you will have the option to withdraw (without any condition,
          consequence or fee) your consent to receive Forge Trust Documents
          electronically.
        </p>
        <br />
        <h3>Withdrawal of Electronic Acceptance of Disclosures and Notices</h3>
        <p>
          You may withdraw your consent to electronically receive Forge Trust
          Documents at any time by updating your Settings in the Client Portal
          or contacting Forge Trust at
          <span style={{ whiteSpace: 'nowrap' }}> (800) 248-8447</span> or
          info@forgetrust.com. You may also use this email address to update
          your information or obtain a paper copy of an electronic record after
          withdrawing consent.
        </p>
        <br />
        <h3>Acknowledgement and Consent</h3>
        <p>
          By clicking <b>&quot;I Consent&quot;</b> - I hereby acknowledge, agree
          and consent to receive Forge Trust Documents (including, without
          limitation, regarding any accounts I hold at Forge Trust, or services
          provided to me) electronically, including by, without limitation,
          email, online portal access and text message. I understand that by not
          selecting this option I may be subject to certain administrative and
          processing fees that may apply to non-electronic documents, and
          processing of matters related to my account may be delayed.
        </p>
        <p>
          By clicking <b>&quot;I Decline&quot;</b> - I do not consent to
          electronically receive Forge Trust Documents. I understand that Forge
          Trust primarily uses electronic processes, so electing this option may
          delay processing of matters associated with my account or any services
          to be provided to me, and that I may be charged certain administrative
          and processing fees if I elect to receive non-electronic (i.e., paper)
          documents by selecting this option, including my quarterly account
          statements.
        </p>
      </div>
      <div className={classes.buttons}>
        {!closeOnly && (
          <>
            <Button
              aria-label="decline"
              disabled={hasScrollBox && !hasScrolled}
              className={classes.narrowButton}
              onClick={() => handleClick(EsignConsentStatus.DENY)}>
              I Decline
            </Button>
            <Button
              aria-label="consent"
              disabled={hasScrollBox && !hasScrolled}
              className={classes.narrowButton}
              onClick={() => handleClick(EsignConsentStatus.ALLOW)}>
              I Consent
            </Button>
          </>
        )}
        {closeOnly && (
          <Button
            aria-label="close"
            disabled={hasScrollBox && !hasScrolled}
            className={classes.narrowButton}
            onClick={() => handleClick()}>
            Close
          </Button>
        )}
      </div>
    </>
  );
};
